import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faPlus,
  faEye,
  faSpinner,
  faTimes,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageSource, setImageSource] = useState('url');
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    image: '',
    tags: '',
    readTime: '',
    products: []
  });
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);

  const token = localStorage.getItem("adminToken");


  // Fetch blogs on component mount
  useEffect(() => {
    fetchBlogs();
  }, []);

  // Fetch products when product modal is shown
  useEffect(() => {
    if (showProductModal) {
      fetchProducts();
    }
  }, [showProductModal]);

  // Filter products based on search and category
  useEffect(() => {
    let results = products;
    
    if (searchTerm) {
      results = results.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory !== 'all') {
      results = results.filter(product => 
        product.category === selectedCategory
      );
    }
    
    setFilteredProducts(results);
  }, [searchTerm, selectedCategory, products]);

  // Fetch blogs from the backend
  const fetchBlogs = async () => {
    try {
      const response = await axios.get(
        'https://afric-fresh-backend.onrender.com/blogs',
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setBlogs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to load blogs. Please try again.');
      setLoading(false);
    }
  };

  // Fetch products from the backend
  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        'https://afric-fresh-backend.onrender.com/admin/products',
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Check if response is successful and contains products array
      if (!response.data.success || !Array.isArray(response.data.products)) {
        throw new Error('Invalid products data format');
      }
  
      const productsData = response.data.products;
      setProducts(productsData);
      setFilteredProducts(productsData);
      
      // Extract unique categories safely
      const categories = [...new Set(
        productsData
          .filter(p => p.category) // Ensure product has category
          .map(p => p.category)
      )];
      setProductCategories(['all', ...categories]); // Add 'all' option for filtering
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products. Please try again.');
      // Set empty arrays to prevent map() errors
      setProducts([]);
      setFilteredProducts([]);
      setProductCategories(['all']);
    }
  };

  // Handle image upload to Cloudinary
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'africfreshupload');

    try {
      const response = await axios.post(
        'https://api.cloudinary.com/v1_1/dfehxeb5r/image/upload',
        formData
      );
      return response.data.secure_url;
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Image upload failed. Please try again.');
      throw error;
    }
  };

  // Add product to selected list
  const handleAddProduct = (product) => {
    if (!selectedProducts.some(p => p.productId === product._id)) {
      setSelectedProducts([...selectedProducts, {
        productId: product._id,
        name: product.name,
        price: product.price,
        image: product.img,
        quantity: 1
      }]);
    }
  };

  // Remove product from selected list
  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.productId !== productId));
  };

  // Update product quantity
  const handleQuantityChange = (productId, value) => {
    setSelectedProducts(selectedProducts.map(p => 
      p.productId === productId ? { ...p, quantity: parseInt(value) || 1 } : p
    ));
  };

  // Save selected products to the blog
  const handleSaveProducts = () => {
    setFormData({ ...formData, products: selectedProducts });
    setShowProductModal(false);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const loadingToast = toast.loading(editingBlog ? 'Updating blog...' : 'Creating blog...');

    try {
      let imageUrl = formData.image;

      // Upload image if file is selected
      if (imageSource === 'upload' && selectedImageFile) {
        imageUrl = await handleImageUpload(selectedImageFile);
      }

      // Prepare data for API
      const data = {
        ...formData,
        image: imageUrl,
        tags: formData.tags.split(',').map(tag => tag.trim()),
        products: selectedProducts
      };

      // Update or create blog
      if (editingBlog) {
        await axios.put(
          `https://afric-fresh-backend.onrender.com/blogs/${editingBlog._id}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        toast.success('Blog updated successfully!');
      } else {
        await axios.post(
          'https://afric-fresh-backend.onrender.com/blogs',
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        toast.success('Blog created successfully!');
      }

      // Reset form and close modal
      setShowModal(false);
      setEditingBlog(null);
      setSelectedImageFile(null);
      setSelectedProducts([]);
      setFormData({
        title: '',
        content: '',
        category: '',
        image: '',
        tags: '',
        readTime: '',
        products: []
      });
      fetchBlogs(); // Refresh the blog list
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error('Error saving blog. Please try again.');
    } finally {
      toast.dismiss(loadingToast);
      setIsSubmitting(false);
    }
  };

  // Handle edit action
  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setImageSource(blog.image.startsWith('http') ? 'url' : 'upload');
    setSelectedProducts(blog.products || []);
    setFormData({
      title: blog.title,
      content: blog.content,
      category: blog.category,
      image: blog.image,
      tags: blog.tags.join(', '),
      readTime: blog.readTime,
    });
    setShowModal(true);
  };

  // Handle delete action
  const handleDelete = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      const loadingToast = toast.loading('Deleting blog...');
      try {
        await axios.delete(
          `https://afric-fresh-backend.onrender.com/blogs/${blogId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        toast.success('Blog deleted successfully!');
        fetchBlogs(); // Refresh the blog list
      } catch (error) {
        console.error('Error deleting blog:', error);
        toast.error('Error deleting blog. Please try again.');
      } finally {
        toast.dismiss(loadingToast);
      }
    }
  };

  return (
    <div className="p-6">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Blog Management</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-[#007D44] text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-[#006435] transition-colors"
        >
          <FontAwesomeIcon icon={faPlus} />
          <span>New Blog</span>
        </button>
      </div>

      {/* Blog List Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Views
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  Loading blogs...
                </td>
              </tr>
            ) : blogs.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No blogs found
                </td>
              </tr>
            ) : (
              blogs.map((blog) => (
                <tr key={blog._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        className="h-10 w-10 rounded-lg object-cover"
                        src={blog.image}
                        alt={blog.title}
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {blog.title}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {blog.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(blog.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {blog.views}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button
                      onClick={() => handleEdit(blog)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      onClick={() => handleDelete(blog._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Blog Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              {editingBlog ? 'Edit Blog' : 'New Blog'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D44]"
                  required
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                <ReactQuill
                  value={formData.content}
                  onChange={(content) =>
                    setFormData({ ...formData, content })
                  }
                  className="h-64 mb-12"
                />
              </div>

              {/* Image Source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Source
                </label>
                <div className="flex gap-4 mb-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="upload"
                      checked={imageSource === 'upload'}
                      onChange={() => setImageSource('upload')}
                      className="mr-2"
                    />
                    Upload Image
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="url"
                      checked={imageSource === 'url'}
                      onChange={() => setImageSource('url')}
                      className="mr-2"
                    />
                    Use Image URL
                  </label>
                </div>

                {imageSource === 'upload' ? (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setSelectedImageFile(e.target.files[0])}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required={!formData.image}
                    />
                    {selectedImageFile && (
                      <p className="mt-2 text-sm text-gray-500">
                        Selected file: {selectedImageFile.name}
                      </p>
                    )}
                  </div>
                ) : (
                  <input
                    type="url"
                    value={formData.image}
                    onChange={(e) =>
                      setFormData({ ...formData, image: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D44]"
                    placeholder="Enter image URL"
                    required={!selectedImageFile}
                  />
                )}
              </div>

              {/* Category and Read Time */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D44]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Read Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.readTime}
                    onChange={(e) =>
                      setFormData({ ...formData, readTime: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D44]"
                    required
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) =>
                    setFormData({ ...formData, tags: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D44]"
                  placeholder="tag1, tag2, tag3"
                />
              </div>

              {/* Add Ingredients Section */}
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">Ingredients</h3>
                  <button
                    type="button"
                    onClick={() => setShowProductModal(true)}
                    className="flex items-center text-[#007D44] hover:text-[#006435]"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Add Ingredient
                  </button>
                </div>

                {/* Selected Products List */}
                {selectedProducts.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {selectedProducts.map(product => (
                      <div key={product.productId} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center">
                          <img 
                            src={product.image} 
                            alt={product.name} 
                            className="w-10 h-10 rounded-md object-cover mr-3"
                          />
                          <div>
                            <p className="font-medium">{product.name}</p>
                            <p className="text-sm text-gray-600">${product.price}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="number"
                            min="1"
                            value={product.quantity}
                            onChange={(e) => handleQuantityChange(product.productId, e.target.value)}
                            className="w-16 px-2 py-1 border rounded text-center mr-3"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveProduct(product.productId)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingBlog(null);
                    setSelectedImageFile(null);
                    setSelectedProducts([]);
                    setFormData({
                      title: '',
                      content: '',
                      category: '',
                      image: '',
                      tags: '',
                      readTime: '',
                      products: []
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#007D44] text-white rounded-lg hover:bg-[#006435] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting && (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  )}
                  {editingBlog ? 'Update' : 'Create'} Blog
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Product Selection Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Select Products</h2>
              <button
                onClick={() => setShowProductModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
            </div>

            {/* Search and Filter */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <FontAwesomeIcon 
                  icon={faSearch} 
                  className="absolute left-3 top-3 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D44]"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D44]"
              >
                <option value="all">All Categories</option>
                {productCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Product List */}
            <div className="flex-1 overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No products found</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredProducts.map(product => (
                    <div 
                      key={product._id} 
                      className={`border rounded-lg p-3 flex items-center cursor-pointer hover:bg-gray-50 ${
                        selectedProducts.some(p => p.productId === product._id) ? 'bg-green-50 border-green-200' : ''
                      }`}
                      onClick={() => handleAddProduct(product)}
                    >
                      <img 
                        src={product.img} 
                        alt={product.name} 
                        className="w-16 h-16 rounded-md object-cover mr-3"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium">{product.name}</h3>
                        <p className="text-sm text-gray-600">${product.price}</p>
                        <p className="text-xs text-gray-500">{product.category}</p>
                      </div>
                      {selectedProducts.some(p => p.productId === product._id) && (
                        <div className="text-green-500 ml-2">
                          <FontAwesomeIcon icon={faPlus} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="mt-4 flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowProductModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveProducts}
                className="px-4 py-2 bg-[#007D44] text-white rounded-lg hover:bg-[#006435]"
              >
                Save Ingredients
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;